import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { NotificationTriggeredByUser } from '../../../reducers/notifications';

/**
 * Shared atoms used by every drawer card. Kept in one file because they're
 * small, tightly coupled to the drawer's visual language, and not consumed
 * anywhere else in the app.
 *
 * Atoms:
 *   <DrawerField>     — one row: label on the left, value on the right
 *   <UserChip>        — avatar + full name; falls back to initials chip
 *   <DrawerDateTime>  — locale-aware exact timestamp + tooltip with ISO
 *   <CategoryLabel>   — translates the category key into the active locale
 */

interface DrawerFieldProps {
    labelId: string;
    labelDefault: string;
    children: React.ReactNode;
}

export const DrawerField: React.FC<DrawerFieldProps> = ({ labelId, labelDefault, children }) => (
    <div className="drawer-card__field">
        <span className="drawer-card__field-label">
            <FormattedMessage id={labelId} defaultMessage={labelDefault} />
        </span>
        <span className="drawer-card__field-value">{children}</span>
    </div>
);

interface UserChipProps {
    user: NotificationTriggeredByUser;
}

export const UserChip: React.FC<UserChipProps> = ({ user }) => {
    const name = `${user.firstname || ''} ${user.lastname || ''}`.trim() || `#${user.id}`;
    // Fallback initials when no avatar — first letter of each name part.
    const initials = ((user.firstname?.[0] || '') + (user.lastname?.[0] || '')).toUpperCase() || '?';
    return (
        <span className="drawer-card__user">
            {user.avatar ? (
                <img src={user.avatar} alt="" className="drawer-card__user-avatar" />
            ) : (
                <span className="drawer-card__user-initials" aria-hidden="true">{initials}</span>
            )}
            <span className="drawer-card__user-name">{name}</span>
        </span>
    );
};

interface DrawerDateTimeProps {
    iso: string;
    locale: string;
}

/**
 * Renders the exact time the user wants to see (locale-aware, full date +
 * time), with the underlying ISO timestamp as a tooltip for power users.
 * Replaces the previous "2 days ago" duplicate — the relative time still
 * lives in the drawer header for context.
 */
export const DrawerDateTime: React.FC<DrawerDateTimeProps> = ({ iso, locale }) => {
    const m = moment(iso);
    if (!m.isValid()) return <span>—</span>;
    const localized = m.locale(locale);
    const display = localized.format('DD MMM YYYY, HH:mm');
    return <span title={iso}>{display}</span>;
};

interface CategoryLabelProps {
    category: string | null;
}

/**
 * Translates the category KEY (e.g. "production", "contact") into the active
 * locale via the existing `notifications.category.*` messages. Previously the
 * drawer showed the raw key — confusing for non-English users.
 */
export const CategoryLabel: React.FC<CategoryLabelProps> = ({ category }) => {
    const intl = useIntl();
    if (!category) return <span>—</span>;
    const id = `notifications.category.${category}`;
    // Use intl.formatMessage so a missing key just falls back to the raw value
    // instead of crashing render. Same behaviour as FormattedMessage's
    // defaultMessage, but lets us compute the id dynamically.
    const translated = intl.formatMessage({ id, defaultMessage: category });
    return <span>{translated}</span>;
};
